import React, { useState } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';
import { toast } from 'react-toastify';

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const SupportCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  margin-bottom: 1rem;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const CategoryCard = styled.div`
  padding: 1.5rem;
  border: 2px solid ${props => props.selected ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 8px;
  background: ${props => props.selected ? props.theme.background : props.theme.backgroundSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    background: ${props => props.theme.background};
  }
`;

const CategoryIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const CategoryTitle = styled.h4`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CategoryDescription = styled.p`
  color: ${props => props.theme.textMuted};
  margin: 0;
  font-size: 0.9rem;
`;

const Form = styled.form`
  background: ${props => props.theme.backgroundSecondary};
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.text};
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
  }
`;

const ErrorMessage = styled.div`
  color: #fa755a;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const CreateTicket = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    attachments: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const categories = [
    {
      id: 'account',
      icon: '👤',
      title: 'Account Issues',
      description: 'Password reset, login problems, account settings'
    },
    {
      id: 'billing',
      icon: '💳',
      title: 'Billing & Payments',
      description: 'Subscription issues, payment problems, refunds'
    },
    {
      id: 'bugs',
      icon: '🐛',
      title: 'Bug Reports',
      description: 'App crashes, errors, unexpected behavior'
    },
    {
      id: 'features',
      icon: '✨',
      title: 'Feature Requests',
      description: 'New features, improvements, suggestions'
    },
    {
      id: 'courses',
      icon: '📚',
      title: 'Courses & Assignments',
      description: 'Course management, assignment tracking, GPA'
    },
    {
      id: 'ai',
      icon: '🤖',
      title: 'AI Features',
      description: 'AI chat, study tools, token usage'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.SUPPORT_TICKET, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          category: selectedCategory,
          subject: formData.subject.trim(),
          description: formData.description.trim(),
          priority: formData.priority
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map(err => err.msg || err.message).join(', ');
          throw new Error(errorMessages);
        }
        throw new Error(data.error || data.message || 'Failed to create support ticket');
      }

      setSuccess('Support ticket created successfully! We\'ll get back to you soon.');
      setSelectedCategory(null);
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
        attachments: []
      });

      toast.success('Support ticket created successfully!');
    } catch (error) {
      setError(error.message);
      toast.error('Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Section>
        <SectionTitle>How can we help you today?</SectionTitle>

        <SupportCard>
          <p style={{ color: '#a0a0a0', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
            We're here to help! Choose a category below and describe your issue or question.
            Our support team will get back to you as soon as possible.
          </p>
        </SupportCard>

        <CategoryGrid>
          {categories.map(category => (
            <CategoryCard
              key={category.id}
              selected={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryTitle>{category.title}</CategoryTitle>
              <CategoryDescription>{category.description}</CategoryDescription>
            </CategoryCard>
          ))}
        </CategoryGrid>

        {selectedCategory && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                name="subject"
                type="text"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Brief description of your issue"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Please provide detailed information about your issue or question..."
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="priority">Priority</Label>
              <Select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="low">Low - General questions or minor issues</option>
                <option value="medium">Medium - Feature requests or moderate issues</option>
                <option value="high">High - App not working or urgent issues</option>
                <option value="critical">Critical - Critical problems affecting multiple users</option>
              </Select>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <ElevatedButton
              type="submit"
              disabled={loading}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              {loading ? 'Creating Ticket...' : 'Create Support Ticket'}
            </ElevatedButton>
          </Form>
        )}
      </Section>
    </div>
  );
};

export default CreateTicket;